import { useEffect, useRef, useState } from "react";
import {
  getBasePath as getBasePathFromPathname,
  getStorePath as getStorePathFromPathname,
  getUrlPathFromPathname,
  sleep,
} from "../../lib/util";
import {
  getBasePath as getBasePathSelector,
  getStorePath as getStorePathSelector,
  getUrlPath,
  setBasePath,
  setStorePath,
  setUrlData,
  setUrlPath,
  updateFullPath,
} from "../slice/path-slice";
import { useAppDispatch, useAppSelector } from "../slice/hooks";

let isPopStateListenerRegistered = false;
let isReloadHandled = false;
let lastNavPath = "";
let lastNavTime = 0;
let lastReplaceTime = 0;

const REPLACE_DEBOUNCE_MS = 100;
const DISPATCH_DEBOUNCE_MS = 0;

export const useHistoryManager = (initialstorePath = "", initialPath = "") => {
  const dispatch = useAppDispatch();
  const urlPath = useAppSelector((state) => getUrlPath(state, initialPath));
  const storePath = useAppSelector((state) =>
    getStorePathSelector(state, initialstorePath),
  );

  const basePath = useAppSelector((state) => getBasePathSelector(state));
  const dispatchTimerRef = useRef(null);
  const pendingDispatchRef = useRef(null);

  const isMobileSafari = () => {
    if (typeof navigator === "undefined") return false;
    return (
      /iP(ad|hone|od)/.test(navigator.userAgent) &&
      /WebKit/.test(navigator.userAgent) &&
      !/CriOS|FxiOS|OPiOS/.test(navigator.userAgent)
    );
  };

  const normalize = (path) => {
    if (!path) return "/";

    try {
      // If the path is relative, prepend a dummy origin so URL parsing works
      const url = new URL(path, window.location.origin);

      // Combine pathname + search + hash
      let full = url.pathname + url.search + url.hash;

      // Remove trailing slashes (except for root "/")
      full = full.replace(/\/+$/, "") || "/";

      return full;
    } catch {
      // If URL parsing fails, fallback to simple normalization
      let full = path.split("?")[0].split("#")[0];
      full = full.replace(/\/+$/, "") || "/";
      return full;
    }
  };

  const throttledReplaceState = (state, path = "") => {
    const now = Date.now();
    if (now - lastReplaceTime < REPLACE_DEBOUNCE_MS) return;
    try {
      window.history.replaceState(state, "", path || undefined);
      lastReplaceTime = now;
    } catch (e) {
      console.warn("Throttled replaceState failed:", e);
    }
  };

  const getCurrentUrlData = (state = window.history.state || {}) => {
    const stack = state.urlDataStack || [];
    return stack.length ? stack[stack.length - 1] : null;
  };

  const restoreScrollFromState = (state) => {
    if (typeof window === "undefined" || isMobileSafari()) return;
    const x = Number.isFinite(state?.scrollX) ? state.scrollX : 0;
    const y = Number.isFinite(state?.scrollY) ? state.scrollY : 0;
    requestAnimationFrame(() => {
      setTimeout(() => window.scrollTo(x, y), 0);
    });
  };

  const setHistoryState = (
    newPath,
    state = {},
    replace = false,
    override = false,
    urlPath = "",
    urlData = null,
  ) => {
    if (typeof window === "undefined") return;

    const currentPath = window.location.pathname + window.location.search;
    if (
      !override &&
      (!newPath || normalize(newPath) === normalize(currentPath))
    )
      return false;

    const currentState = window.history.state || {};
    const { scrollX, scrollY } = window;

    const existingStack = Array.isArray(currentState.urlDataStack)
      ? [...currentState.urlDataStack]
      : currentState.urlData
        ? [currentState.urlData]
        : [];

    if (replace && existingStack.length) {
      if (urlData !== undefined && urlData !== null) {
        existingStack[existingStack.length - 1] = urlData; // replace top
      }
    } else {
      if (newPath && normalize(newPath) != normalize(currentPath)) {
        existingStack.push(urlData); // push new
      } else {
        if (existingStack.length) {
          existingStack[existingStack.length - 1] = urlData; // replace top
        } else {
          existingStack.push(urlData);
        }
      }
    }

    if (existingStack.length > 50) {
      existingStack.shift();
    }

    const newState = {
      basePath: currentState.basePath || undefined,
      ...state,
      newPath,
      currentPath,
      scrollX,
      scrollY,
      urlPath,
      urlData: existingStack.length
        ? existingStack[existingStack.length - 1]
        : null,
      urlDataStack: existingStack,
    };

    if (replace) {
      window.history.replaceState(newState, "", newPath);
      return newState;
    }

    if (!override && currentState.modal) {
      if (!newState.basePath) newState.basePath = currentPath;
      window.history.replaceState(newState, "", newPath);
    } else {
      if (!override) newState.basePath = currentPath;
      if (newPath && normalize(newPath) != normalize(currentPath)) {
        window.history.pushState(newState, "", newPath);
      } else {
        if(state.modal){
          window.history.pushState(newState, "", newPath);
        }else{
           window.history.replaceState(newState, "", newPath);
        }
      }
    }

    return newState;
  };

  const revertModalHistory = (trigger = "") => {
    if (typeof window === "undefined") return;
    if (trigger !== "backButton") window.history.back();
  };

  const markModalInHistory = () => {
    if (typeof window === "undefined") return;
    const currentState = window.history.state || {};
    const state = { ...currentState, modal: true };
    const fullPath = window.location.pathname + window.location.search;

    if (currentState.modal) {
      setHistoryState(fullPath, state, true, true);
      return;
    }

    setHistoryState(fullPath, state, false, true);
  };

  const resetUrl = (state = {}) => {
    if (typeof window === "undefined") return;
    const currentPath = window.location.pathname + window.location.search;
    const { basePath, currentPath: prevPath, modal } = state;
    const currentUrlData = getCurrentUrlData(state);
    const targetPath = modal
      ? prevPath || basePath || currentPath
      : currentPath;
    navigate(
      targetPath,
      { replace: true, modal: false, urlData: currentUrlData },
      true,
    );
  };

  const navigateTo = async (path, options = {}, preFixStorePath = true) => {
    const fullPath = cleanNavigationPath(path, preFixStorePath);
    const currentState = window.history.state || {};
    if (currentState.modal) {
      revertModalHistory();
      await sleep(300);
    }

    return await navigate(fullPath, options);
  };

  const cleanNavigationPath = (path, preFixBasePath = true) => {
    const isHome = !path || path === "home" || path === "/";
    const trimmedPath = path?.trim().replace(/\/+$/, "") || "";

    let fullPath;

    if (isHome) {
      // Go straight to the store root
      fullPath = storePath;
    } else if (preFixBasePath) {
      // If path already starts with basePath (with or without leading slash)
      if (
        trimmedPath.startsWith(basePath) ||
        trimmedPath.startsWith(`/${basePath}`)
      ) {
        // Strip extra slashes at the front
        fullPath = trimmedPath.replace(/^\/+/, "");
      } else {
        // Safely concatenate storePath + trimmedPath
        const base = storePath.replace(/\/+$/, "");
        const loc = trimmedPath.replace(/^\/+/, "");
        fullPath = `${base}/${loc}`;
      }
    } else {
      fullPath = trimmedPath;
    }

    // Ensure it always starts with /
    if (!fullPath.startsWith("/")) {
      fullPath = `/${fullPath}`;
    }

    return fullPath;
  };

  const navigate = async (fullPath, options = {}, backButton = false) => {
    const {
      state = {},
      replace = false,
      override = false,
      debounceMs = 300,
      onNavigate = null,
      urlData = null,
      dispatchUrl = true,
    } = options;

    if (shouldSkipNavigation(fullPath, debounceMs)) return;
    const urlPath = getUrlPathFromPathname(fullPath);
    const storePath = getStorePathFromPathname(fullPath);
    const basePath = getBasePathFromPathname(fullPath);
    const newRoute = setHistoryState(
      fullPath,
      state,
      replace,
      override,
      urlPath,
      urlData,
    );

    if (newRoute || backButton) {
      pendingDispatchRef.current = { urlPath, urlData, storePath, basePath };
      const delay = DISPATCH_DEBOUNCE_MS;
      if (dispatchTimerRef.current) {
        clearTimeout(dispatchTimerRef.current);
      }
      dispatchTimerRef.current = setTimeout(() => {
        const payload = pendingDispatchRef.current;
        if (!payload) return;
        dispatch(setUrlPath(payload.urlPath));
        dispatch(setUrlData(payload.urlData));
        dispatch(setStorePath(payload.storePath));
        dispatch(setBasePath(payload.basePath));
      }, delay);
    }

    onNavigate?.(!!newRoute, fullPath, newRoute);
    return newRoute;
  };

  const shouldSkipNavigation = (path, debounceMs = 300) => {
    const now = Date.now();
    if (
      normalize(path) === normalize(lastNavPath) &&
      now - lastNavTime < debounceMs
    )
      return true;

    lastNavPath = path;
    lastNavTime = now;
    return false;
  };

  const exitPathOnLoad = (guardPath, condition) => {
    if (typeof window === "undefined") return;
    if (guardPath === urlPath && condition) {
      navigateTo("home", { state: { modal: false } });
    }
  };

  // Popstate listener
  useEffect(() => {
    if (typeof window === "undefined" || isPopStateListenerRegistered) return;

    const handlePopState = (event) => {
      const state = event.state || {};
      resetUrl(state);
      restoreScrollFromState(state);
    };

    window.addEventListener("popstate", handlePopState);
    isPopStateListenerRegistered = true;
  }, []);

  useEffect(() => {
    return () => {
      if (dispatchTimerRef.current) {
        clearTimeout(dispatchTimerRef.current);
      }
    };
  }, []);

  // Handle reload: mark modal false and restore scroll
  useEffect(() => {
    if (typeof window === "undefined" || isReloadHandled) return;

    try {
      const [entry] = performance.getEntriesByType("navigation") || [];
      const navType =
        entry?.type ||
        (performance.navigation?.type === 1 ? "reload" : "navigate");

      if (navType === "reload" || navType === "navigate") {
        const state = window.history.state || {};
        const newState = state.modal ? { ...state, modal: false } : state;
        const fullPath = window.location.pathname + window.location.search;
        setHistoryState(fullPath, newState, true, true);
        restoreScrollFromState(state);
      }
    } catch {}

    isReloadHandled = true;
  }, []);

  // Save scroll on unload
  useEffect(() => {
    if (typeof window === "undefined") return;

    const saveScrollOnUnload = () => {
      const x = Math.round(window.scrollX || 0);
      const y = Math.round(window.scrollY || 0);
      const state = window.history.state || {};
      throttledReplaceState({ ...state, scrollX: x, scrollY: y });
    };

    window.addEventListener("beforeunload", saveScrollOnUnload);
    return () => window.removeEventListener("beforeunload", saveScrollOnUnload);
  }, []);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      "scrollRestoration" in window.history
    ) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Save scroll during normal scroll events
  useEffect(() => {
    if (typeof window === "undefined") return;

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const x = Math.round(window.scrollX || 0);
          const y = Math.round(window.scrollY || 0);
          const state = window.history.state || {};
          throttledReplaceState({ ...state, scrollX: x, scrollY: y });
          // window.history.replaceState({ ...state, scrollX: x, scrollY: y }, "", window.location.pathname);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Reset debounce state on unmount
  useEffect(() => {
    return () => {
      lastNavPath = "";
      lastNavTime = 0;
    };
  }, []);

  return {
    setHistoryState,
    revertModalHistory,
    markModalInHistory,
    resetUrl,
    navigateTo,
    navigate,
    exitPathOnLoad,
    shouldSkipNavigation,
    cleanNavigationPath,
  };
};
